import api from '../../utils/api';
import { ADMIN_INVOICE_ANALYTICS, ADMIN_INVOICES } from '../../constants/routes';
import {
  AdminInvoiceAnalyticsParams,
  AdminInvoiceAnalyticsResponse,
  AdminInvoiceListParams,
  AdminInvoiceListResponse,
} from '../../types/adminInvoice';

type ApiResult<T> = { data: T | null; status: number; error?: string; aborted?: boolean };

function isRequestAborted(error: unknown): boolean {
  const err = error as { code?: string; name?: string; message?: string };
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    (typeof err?.message === 'string' && /cancel/i.test(err.message))
  );
}

function parseApiError(error: unknown, fallback: string): ApiResult<never> {
  if (isRequestAborted(error)) {
    return { data: null, status: 0, aborted: true };
  }
  const err = error as { response?: { status?: number; data?: { detail?: string; message?: string } } };
  const status = err.response?.status ?? 500;
  const detail = err.response?.data?.detail ?? err.response?.data?.message;
  return {
    data: null,
    status,
    error: typeof detail === 'string' ? detail : fallback,
  };
}

export async function fetchAdminInvoices(
  params: AdminInvoiceListParams = {}
): Promise<ApiResult<AdminInvoiceListResponse>> {
  try {
    const query: Record<string, string | number> = {
      limit: params.limit ?? 10,
      offset: params.offset ?? 0,
    };
    if (params.search?.trim()) query.search = params.search.trim();

    const sport = params.sport_code?.trim();
    const conference = params.conference_name?.trim();
    if (sport) query.sport_code = sport;
    if (conference) query.conference_name = conference;

    const response = await api.get<AdminInvoiceListResponse>(ADMIN_INVOICES, { params: query });
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load invoices');
  }
}

export async function fetchAdminInvoiceAnalytics(
  params: AdminInvoiceAnalyticsParams,
  signal?: AbortSignal
): Promise<ApiResult<AdminInvoiceAnalyticsResponse>> {
  try {
    const query: Record<string, string> = {
      date_from: params.date_from,
      date_to: params.date_to,
    };
    if (params.source) query.source = params.source;
    if (params.granularity) query.granularity = params.granularity;
    const sport = params.sport_code?.trim();
    const conference = params.conference_name?.trim();
    if (sport) query.sport_code = sport;
    if (conference) query.conference_name = conference;

    const response = await api.get<AdminInvoiceAnalyticsResponse>(ADMIN_INVOICE_ANALYTICS, {
      params: query,
      signal,
    });
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load invoice analytics');
  }
}
